import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  TextField,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';

/**
 * Form to collect optional user metadata before the interview.
 */
const MetadataForm = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out empty fields
    const metadata = Object.fromEntries(
      Object.entries(formData).filter(([_, v]) => v.trim() !== '')
    );
    onSubmit(Object.keys(metadata).length > 0 ? metadata : null);
  };

  const handleSkip = () => {
    onSubmit(null);
  };

  return (
    <Card
      sx={{
        maxWidth: 500,
        mx: 'auto',
        mt: 4,
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <IconButton onClick={onBack} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Before we begin
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Tell us a bit about yourself (optional)
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          <TextField
            name="name"
            label="Your Name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            placeholder="e.g., John Smith"
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TextField
            name="email"
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            placeholder="e.g., john@company.com"
          />

          <TextField
            name="company"
            label="Company / Organization"
            value={formData.company}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            placeholder="e.g., ABC Property Management"
          />

          <TextField
            name="role"
            label="Your Role"
            value={formData.role}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            placeholder="e.g., Property Manager"
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleSkip}
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              Skip
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                }
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 3 }}
        >
          This information helps us personalize your experience.
          All fields are optional.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MetadataForm;
